import { Accordion, AccordionDetails, AccordionSummary, Paper, Stack } from '@mui/material'
import { useGetCustomer } from '@src/api/customer-api'
import { useGetCalendarSimulation } from '@src/api/loan/loan-api'
import PageError from '@src/components/Common/Error/PageError'
import StickyDrawer from '@src/components/Common/Sticky/StickyDrawer'
import PageContainer from '@src/components/Layout/PageContainer'
import PageHeader from '@src/components/Layout/PageHeader'
import PageLoader from '@src/components/Loader/PageLoader'
import LoanLastTransactionCardContent from '@src/components/LoanCalendar/Card/LoanLastTransactionCardContent'
import LoanLateStatusCardContent from '@src/components/LoanCalendar/Card/LoanLateStatusCardContent'
import LoanCreditReportCardContent from '@src/components/LoanCalendar/Card/LoanMetro2CardContent'
import LoanNextPaymentCardContent from '@src/components/LoanCalendar/Card/LoanNextPaymentCardContent'
import LoanStatusCardContent from '@src/components/LoanCalendar/Card/LoanStatusCardContent'
import LoanCalendarActionProvider from '@src/components/LoanCalendar/LoanCalendarActionProvider'
import LoanCalendarActions from '@src/components/LoanCalendar/LoanCalendarActions'
import LoanCalendarSimulationProvider, {
  useLoanCalendarSimulation,
} from '@src/components/LoanCalendar/LoanCalendarSimulationProvider'
import LoanCalendarTable from '@src/components/LoanCalendar/LoanCalendarTable/LoanCalendarTable'
import LoanLockProvider, { useLoanLock } from '@src/providers/LoanLockProvider'
import { useAlertBanner } from '@src/contexts/alert-banner-context'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const LoanCalendarStatusCards = () => {
  const { t } = useTranslation()
  const { currentSimulation } = useLoanCalendarSimulation()
  const simulation = currentSimulation.simulation

  return (
    <StickyDrawer>
      <Accordion defaultExpanded>
        <AccordionSummary>{t('loan.payment.statusCard.title')}</AccordionSummary>
        <AccordionDetails>
          <LoanStatusCardContent simulation={simulation} />
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>{t('loan.payment.lateStatusCard.title')}</AccordionSummary>
        <AccordionDetails>
          <LoanLateStatusCardContent simulation={simulation} />
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>{t('loan.payment.creditReportCard.title')}</AccordionSummary>
        <AccordionDetails>
          <LoanCreditReportCardContent signedSimulation={currentSimulation} />
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>{t('loan.payment.nextPaymentCard.title')}</AccordionSummary>
        <AccordionDetails>
          <LoanNextPaymentCardContent threeNextPayments={simulation.getThreeNextPlannedPayments()} />
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded>
        <AccordionSummary>{t('loan.payment.lastTransactionCard.title')}</AccordionSummary>
        <AccordionDetails>
          <LoanLastTransactionCardContent summaryInformation={simulation.summaryInformation} />
        </AccordionDetails>
      </Accordion>
    </StickyDrawer>
  )
}

const SystemLockBanner = () => {
  const { isLockedBySystem } = useLoanLock()
  const { showBanner, hideBanner } = useAlertBanner()
  const { t } = useTranslation()

  useEffect(() => {
    if (isLockedBySystem) {
      showBanner(t('common.lockTooltip.lockedBySystem'))
    }
    return () => hideBanner()
  }, [isLockedBySystem, showBanner, hideBanner, t])

  return null
}

interface Props {
  id: string
  financingProgramId: string
}

const LoanCalendarPage = ({ id, financingProgramId }: Props) => {
  const { t } = useTranslation()
  const [signedSimulation, isPending, error, refetch] = useGetCalendarSimulation(id, financingProgramId)
  const simulation = signedSimulation?.simulation
  const [customerData, isCustomerPending, customerError] = useGetCustomer(simulation?.fullLoan.loan.borrowerId)

  const handleRefetch = useCallback(() => {
    void refetch()
  }, [refetch])

  if (isPending || isCustomerPending) return <PageLoader />
  if (!simulation || !customerData || error || customerError) return <PageError error={error || customerError} />

  return (
    <>
      <LoanCalendarSimulationProvider originalSignedSimulation={signedSimulation} refetch={handleRefetch}>
        <LoanLockProvider>
          <SystemLockBanner />
          <PageHeader
            breadcrumbs={{
              current: t('loan.payment.title'),
              path: [
                { href: '/loans', label: t('loan.title') },
                { href: '..', label: t('loan.details.title') },
              ],
            }}
            title={`${customerData.firstName} ${customerData.lastName} - ${simulation?.fullLoan.loan.loanReferenceId}`}
            actions={<LoanCalendarActions simulation={simulation} />}
          />
          <LoanCalendarActionProvider>
            <Stack direction="row">
              <LoanCalendarStatusCards />
              <PageContainer sx={{ pt: 1, minWidth: 0, overflow: 'auto' }} smallPdLeft>
                <Paper>
                  <LoanCalendarTable />
                </Paper>
              </PageContainer>
            </Stack>
          </LoanCalendarActionProvider>
        </LoanLockProvider>
      </LoanCalendarSimulationProvider>
    </>
  )
}

export default LoanCalendarPage
